// Client-side security utilities

interface SecureStorageOptions {
  encrypt?: boolean;
  expiry?: number; // milliseconds
}

/**
 * Secure storage wrapper with encryption and expiry
 */
export class SecureStorage {
  private static encrypt(data: string): string {
    // Simple base64 encoding for basic obfuscation
    // In production, consider using Web Crypto API for real encryption
    return btoa(encodeURIComponent(data));
  }

  private static decrypt(data: string): string {
    try {
      return decodeURIComponent(atob(data));
    } catch {
      return data; // Return as-is if decryption fails
    }
  }

  static setItem(key: string, value: any, options: SecureStorageOptions = {}): void {
    try {
      const data = {
        value: typeof value === 'string' ? value : JSON.stringify(value),
        timestamp: Date.now(),
        expiry: options.expiry ? Date.now() + options.expiry : null
      };

      let serialized = JSON.stringify(data);
      
      if (options.encrypt) {
        serialized = this.encrypt(serialized);
      }

      localStorage.setItem(key, serialized);
    } catch (error) {
      console.warn('SecureStorage.setItem failed:', error);
    }
  }

  static getItem(key: string, encrypted = false): any {
    try {
      let item = localStorage.getItem(key);
      if (!item) return null;

      if (encrypted) {
        item = this.decrypt(item);
      }

      const data = JSON.parse(item);
      
      // Check expiry
      if (data.expiry && Date.now() > data.expiry) {
        this.removeItem(key);
        return null;
      }

      try {
        return JSON.parse(data.value);
      } catch {
        return data.value;
      }
    } catch (error) {
      console.warn('SecureStorage.getItem failed:', error);
      return null;
    }
  }

  static removeItem(key: string): void {
    localStorage.removeItem(key);
  }

  static clear(): void {
    localStorage.clear();
  }

  /**
   * Clean up expired items
   */
  static cleanup(): void {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      try {
        const item = localStorage.getItem(key);
        if (item) {
          const data = JSON.parse(item);
          if (data.expiry && Date.now() > data.expiry) {
            localStorage.removeItem(key);
          }
        }
      } catch {
        // Skip malformed items
      }
    });
  }
}

/**
 * Rate limiter for client-side operations
 */
export class ClientRateLimiter {
  private static attempts: Map<string, number[]> = new Map();

  static isAllowed(key: string, maxAttempts = 5, windowMs = 60000): boolean {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Remove old attempts outside the window
    const validAttempts = attempts.filter(time => now - time < windowMs);
    
    if (validAttempts.length >= maxAttempts) {
      return false;
    }
    
    validAttempts.push(now);
    this.attempts.set(key, validAttempts);
    
    return true;
  }

  static reset(key: string): void {
    this.attempts.delete(key);
  }
}

/**
 * Input sanitizer for user input
 */
export function sanitizeInput(input: string): string {
  if (typeof input !== 'string') return '';
  
  return input
    .trim()
    .replace(/[<>'"&]/g, '') // Remove XSS-prone characters
    .replace(/\s+/g, ' ') // Normalize whitespace
    .substring(0, 1000); // Limit length
}

/**
 * Session timeout manager
 */
export class SessionManager {
  private static timeoutId: number | null = null;
  private static warningTimeoutId: number | null = null;
  private static readonly TIMEOUT_DURATION = 24 * 60 * 60 * 1000; // 24 hours
  private static readonly WARNING_DURATION = 23 * 60 * 60 * 1000; // 23 hours
  private static onTimeout?: () => void;
  private static onWarning?: () => void;

  static init(onTimeout: () => void, onWarning?: () => void): void {
    this.onTimeout = onTimeout;
    this.onWarning = onWarning;
    this.reset();
  }

  static reset(): void {
    if (this.timeoutId) clearTimeout(this.timeoutId);
    if (this.warningTimeoutId) clearTimeout(this.warningTimeoutId);

    this.timeoutId = window.setTimeout(() => {
      this.onTimeout?.();
    }, this.TIMEOUT_DURATION);

    if (this.onWarning) {
      this.warningTimeoutId = window.setTimeout(() => {
        this.onWarning?.();
      }, this.WARNING_DURATION);
    }
  }

  static destroy(): void {
    if (this.timeoutId) clearTimeout(this.timeoutId);
    if (this.warningTimeoutId) clearTimeout(this.warningTimeoutId);
    this.timeoutId = null;
    this.warningTimeoutId = null;
  }
}

/**
 * Initialize client-side security measures
 */
export function initClientSecurity(): void {
  // Clean up expired storage items on load
  SecureStorage.cleanup();

  // Set up periodic cleanup
  setInterval(() => {
    SecureStorage.cleanup();
  }, 5 * 60 * 1000); // Every 5 minutes

  // Add CSP headers warning
  if (!document.querySelector('meta[http-equiv="Content-Security-Policy"]')) {
    console.warn('Consider adding Content Security Policy headers for enhanced security');
  }
}